/**
 * Leitner-style spaced repetition scheduler.
 *
 * Every graded answer moves a word between boxes; the box decides when the
 * word comes back. Everything here is pure — the store lives in
 * `hooks/useSrs.ts`, the side effects in `lib/study.ts` — so the schedule can
 * be unit-tested without touching localStorage or React.
 */

import type { Mastery } from "@/types/lex";

/** How well the learner recalled a word. Drills map correct → good, wrong → again. */
export type Grade = "again" | "hard" | "good" | "easy";

export interface SrsRecord {
  /** Leitner box: 0 = just failed, MAX_BOX = longest interval. */
  box: number;
  /** Epoch ms at which the word becomes due again. */
  due: number;
  /** Epoch ms of the last grading, 0 for a record seeded from a manual label. */
  last: number;
  /** Total gradings, including failures. */
  reps: number;
  /** Times the word fell out of a box it had already reached. */
  lapses: number;
}

export const MAX_BOX = 5;
export const DAY_MS = 86_400_000;

/** Days between reviews per box. Box 0 is special-cased to `AGAIN_MS`. */
export const BOX_DAYS: readonly number[] = [0, 1, 3, 7, 14, 30];

/** A failed word comes back within the same sitting rather than tomorrow. */
export const AGAIN_MS = 10 * 60 * 1000;

export function intervalMs(box: number): number {
  if (box <= 0) return AGAIN_MS;
  return BOX_DAYS[Math.min(box, MAX_BOX)] * DAY_MS;
}

export function clampBox(box: number): number {
  return Math.max(0, Math.min(MAX_BOX, Math.round(box)));
}

export function nextBox(box: number, grade: Grade): number {
  switch (grade) {
    case "again":
      return 0;
    case "hard":
      return clampBox(box - 1);
    case "good":
      return clampBox(box + 1);
    case "easy":
      return clampBox(box + 2);
  }
}

/**
 * Grade a word. A missing record is treated as a fresh box-0 word, so the
 * first answer in any drill creates the schedule.
 */
export function applyGrade(rec: SrsRecord | undefined, grade: Grade, now: number): SrsRecord {
  const prevBox = rec?.box ?? 0;
  const box = nextBox(prevBox, grade);
  // Only count a lapse when the word actually fell from a box it had earned.
  const lapsed = grade === "again" && prevBox > 0;
  return {
    box,
    due: now + intervalMs(box),
    last: now,
    reps: (rec?.reps ?? 0) + 1,
    lapses: (rec?.lapses ?? 0) + (lapsed ? 1 : 0),
  };
}

export function isDue(rec: SrsRecord | undefined, now: number): boolean {
  return rec != null && rec.due <= now;
}

/** Ledger colour for a box — mirrors the manual Unknown / Learning / Known control. */
export function masteryFromBox(box: number): Exclude<Mastery, "untouched"> {
  if (box <= 0) return "unknown";
  if (box <= 2) return "learning";
  return "known";
}

/** Where a word starts when the learner labels it by hand instead of drilling it. */
export function boxFromMastery(value: Exclude<Mastery, "untouched">): number {
  if (value === "unknown") return 0;
  if (value === "learning") return 1;
  return 3;
}

/**
 * Local calendar day, `YYYY-MM-DD`. Deliberately local rather than UTC: a
 * study day ends at the learner's midnight, not Greenwich's.
 */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function shiftDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

/**
 * Consecutive study days ending today. Today being empty doesn't break the
 * streak until the day is over, so counting starts at yesterday in that case.
 */
export function streakFrom(days: ReadonlySet<string>, today: Date): number {
  let cursor = days.has(dayKey(today)) ? today : shiftDays(today, -1);
  let n = 0;
  while (days.has(dayKey(cursor))) {
    n += 1;
    cursor = shiftDays(cursor, -1);
  }
  return n;
}

/**
 * Human-readable countdown for a due date: "now", "in 4h", "in 3d".
 */
export function dueLabel(due: number, now: number): string {
  const delta = due - now;
  if (delta <= 0) return "now";
  const hours = Math.round(delta / 3_600_000);
  if (hours < 1) return "< 1h";
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(delta / DAY_MS)}d`;
}
